import {
  estimateDataUrlByteSize as estimateFromModule,
  normalizePositiveByteSize,
} from '@/utils/imageSpaceByteSize';
import { resolveImageSpaceSourceKey } from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';

const COLLECTION_SOURCES = new Set([
  'sourcing',
  'materialCrawlerUser',
  'materialCrawlerScheduled',
]);

/**
 * 判断是否为采集来源（插件采集 / 用户采集 / 定时采集）。
 * @param {string | Record<string, unknown> | null | undefined} sourceOrImage 来源字符串或图片记录
 * @returns {boolean}
 */
export function isImageSpaceCollectionSource(sourceOrImage) {
  const key = sourceOrImage != null && typeof sourceOrImage === 'object'
    ? resolveImageSpaceSourceKey(sourceOrImage)
    : String(sourceOrImage || '');
  return COLLECTION_SOURCES.has(key);
}

export function estimateDataUrlByteSize(dataUrl) {
  return estimateFromModule(dataUrl);
}

export function resolveImageDisplayByteSize(image, src = '') {
  const fields = [image?.size, image?.fileSize, image?.byteSize];
  for (const raw of fields) {
    const n = normalizePositiveByteSize(raw);
    if (n != null) return n;
  }
  const candidates = [src, image?.src, image?.url, image?.dataUrl].filter(
    (item) => typeof item === 'string' && item.trim(),
  );
  for (const candidate of candidates) {
    const estimated = estimateFromModule(candidate);
    if (estimated) return estimated;
  }
  return null;
}

export function resolveImageByteSize(image) {
  return resolveImageDisplayByteSize(image) || 0;
}

export function computeImageSpaceStorageBreakdown(images) {
  const list = Array.isArray(images) ? images : [];
  let collectionBytes = 0;
  let materialBytes = 0;
  let trashBytes = 0;

  for (const image of list) {
    const bytes = resolveImageByteSize(image);
    if (!bytes) continue;
    if (image.deletedAt) {
      trashBytes += bytes;
      continue;
    }
    if (isImageSpaceCollectionSource(image)) {
      collectionBytes += bytes;
    } else {
      materialBytes += bytes;
    }
  }

  const totalBytes = collectionBytes + materialBytes + trashBytes;
  return {
    collectionBytes,
    materialBytes,
    trashBytes,
    totalBytes,
  };
}

export function bytesToMb(bytes) {
  const n = Number(bytes) || 0;
  return n / (1024 * 1024);
}

export function formatStorageBytesLabel(bytes, { digits = 2 } = {}) {
  const n = Number(bytes) || 0;
  if (n <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / (1024 ** idx);
  const fixed = value >= 100 || idx === 0
    ? Math.round(value)
    : Number(value.toFixed(digits));
  return `${fixed}${units[idx]}`;
}

export function formatStorageMbLabel(mb, { digits = 2 } = {}) {
  const n = Number(mb) || 0;
  if (n <= 0) return '0MB';
  if (n >= 1024) {
    const gb = n / 1024;
    return `${Number(gb.toFixed(digits))}GB`;
  }
  return `${Number(n.toFixed(digits))}MB`;
}

export function calcStoragePercent(usedBytes, limitBytes) {
  const used = Number(usedBytes) || 0;
  const limit = Number(limitBytes) || 0;
  if (limit <= 0) return 0;
  return Math.min(100, (used / limit) * 100);
}

/** 账户中心 quota 中的已用容量（字节），无有效值时返回 null */
export function resolveQuotaUsedBytes(quota) {
  const mb = Number(quota?.storageUsedMb ?? quota?.usedStorageMb);
  if (!Number.isFinite(mb) || mb <= 0) return null;
  return mb * 1024 * 1024;
}
